import React, { useEffect, useRef, useState } from 'react';
import { useDismissOnOutsideClick } from '../../hooks/useDismissOnOutsideClick';
import { useResourceNotes, useCreateNote, useDeleteNote, useSearchTermHighlights, useSearchHits } from '../../hooks/useResource';
import { useSearchStore } from '../../stores/searchStore';
import { useResourceViewerStore } from '../../stores/resourceViewerStore';
import { useTagRelated } from '../../stores/tagRelatedStore';
import TaggingForm from './TaggingForm';
import type { Resource } from '../../types/resource';

export interface CodingTabProps {
  resource: Resource;
  projectId: string;
  collectionId: string;
  resourceId: string;
  onSearchTermSelect?: (snippetText: string) => void;
}

// Snippets are raw document text with <em> highlight markers injected by the search
// backend. Anything else that looks like a tag (e.g. "<mmeyers@KPTV.com>") is literal
// text, so render as React nodes instead of HTML.
function renderSnippet(snippet: string): React.ReactNode[] {
  return snippet.split(/(<em>[\s\S]*?<\/em>)/g).map((part, i) =>
    part.startsWith('<em>')
      ? <em key={i}>{part.slice(4, -5)}</em>
      : <React.Fragment key={i}>{part}</React.Fragment>
  );
}

export default function CodingTab({ resource, projectId, collectionId, resourceId, onSearchTermSelect }: CodingTabProps) {
  const { data: notes = [] } = useResourceNotes(projectId, collectionId, resourceId);
  const createNote = useCreateNote(projectId, collectionId, resourceId);
  const deleteNote = useDeleteNote(projectId, collectionId, resourceId);

  const [noteText, setNoteText] = useState('');
  // An unsent note counts as a pending change (mirrors Angular's newResourceNote.note watch).
  const setPendingChange = useResourceViewerStore((s) => s.setPendingChange);
  useEffect(() => {
    setPendingChange('notes', noteText !== '');
  }, [noteText, setPendingChange]);
  useEffect(() => () => setPendingChange('notes', false), [setPendingChange]);
  // "Disregard Changes" drops the draft, as a successful save does.
  const disregardCount = useResourceViewerStore((s) => s.disregardCount);
  const seenDisregard = React.useRef(disregardCount);
  useEffect(() => {
    if (disregardCount === seenDisregard.current) return;
    seenDisregard.current = disregardCount;
    setNoteText('');
  }, [disregardCount]);
  const [noteCategory, setNoteCategory] = useState<'Regular' | 'Privileged'>('Regular');
  const [noteCategoryOpen, setNoteCategoryOpen] = useState(false);
  const noteCategoryRef = useRef<HTMLDivElement>(null);
  useDismissOnOutsideClick(noteCategoryRef, noteCategoryOpen, () => setNoteCategoryOpen(false));
  const [expandedLists, setExpandedLists] = useState<Set<string>>(new Set());
  const [tagRelated, setTagRelated] = useTagRelated();
  const [selectedTermId, setSelectedTermId] = useState<string | null>(null);
  const [selectedSnippetIdx, setSelectedSnippetIdx] = useState<number | null>(null);
  // Angular kept this index separate from the term-highlights one ($selectedSnippetIndex
  // vs termHighlights.selectedIndex), so the two lists highlight independently.
  const [selectedHitIdx, setSelectedHitIdx] = useState<number | null>(null);

  const { data: highlights = [], isFetching: highlightsFetching } = useSearchTermHighlights(
    projectId, collectionId, resourceId, selectedTermId
  );

  const search = useSearchStore((s) => s.search);
  const { data: searchHits = [] } = useSearchHits(projectId, collectionId, resourceId, search);

  function handleNoteSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!noteText.trim()) return;
    createNote.mutate({ note: noteText.trim(), noteCategoryName: noteCategory }, {
      onSuccess: () => setNoteText(''),
    });
  }

  return (
    <div style={{ overflowY: 'auto', maxHeight: '100%', padding: '10px' }}>
      <TaggingForm
        resource={resource}
        projectId={projectId}
        collectionId={collectionId}
        resourceId={resourceId}
        tagRelated={tagRelated}
        onTagRelatedChange={setTagRelated}
      />
      <hr />

      <h4>Notes</h4>
      <form onSubmit={handleNoteSubmit}>
        <textarea
          className="form-control"
          style={{ width: '90%' }}
          data-testid="viewer-note-input"
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          disabled={createNote.isPending}
          rows={3}
        />
        <br />
        <div ref={noteCategoryRef} className={`btn-group${noteCategoryOpen ? ' open' : ''}`} style={{ marginRight: 8 }}>
          <button
            type="button"
            className="btn btn-default dropdown-toggle"
            onClick={() => setNoteCategoryOpen((v) => !v)}
            disabled={createNote.isPending}
            data-testid="viewer-note-type-btn"
          >
            {noteCategory === 'Regular' ? 'Regular' : 'Privileged Log Note'}
            {' '}<span className="caret" />
          </button>
          <ul className="dropdown-menu" data-testid="viewer-note-type-menu">
            <li>
              <a onMouseDown={() => { setNoteCategory('Regular'); setNoteCategoryOpen(false); }} data-testid="viewer-note-type-regular-menu-item">
                <i className={`far ${noteCategory === 'Regular' ? 'fa-check-square' : 'fa-square'}`} /> Regular
              </a>
            </li>
            <li>
              <a onMouseDown={() => { setNoteCategory('Privileged'); setNoteCategoryOpen(false); }} data-testid="viewer-note-type-privileged-menu-item">
                <i className={`far ${noteCategory === 'Privileged' ? 'fa-check-square' : 'fa-square'}`} /> Privileged Log Note
              </a>
            </li>
          </ul>
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={createNote.isPending || !noteText.trim()}
          data-testid="viewer-add-note-btn"
        >
          {createNote.isPending && <i className="fas fa-spinner fa-spin" />}
          {' '}Add note <i className="fas fa-plus-circle" style={{ color: '#fff' }} />
        </button>
      </form>

      {notes.length === 0 ? (
        <div className="alert alert-info alert-block" style={{ marginTop: 12 }}>
          <h4>No notes found</h4>
          <p>This document does not have any notes</p>
        </div>
      ) : (
        <table className="table table-hover table-condensed table-iio" style={{ marginTop: 12 }} data-testid="viewer-notes-table">
          <tbody>
            {notes.map((note) => (
              <tr key={note.id} data-testid="viewer-note-row">
                <td style={{ whiteSpace: 'nowrap' }}>
                  <span title={note.accountFullName}>{note.accountFirstName}</span>
                  <br />
                  <span className={`label ${note.noteCategoryName === 'Privileged' ? 'label-danger' : 'label-default'}`}>
                    {note.noteCategoryName}
                  </span>
                  {' '}
                  <small className="text-muted">{note.createdAt}</small>
                </td>
                <td>{note.note}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-xs btn-danger"
                    onClick={() => deleteNote.mutate(note.id)}
                    data-testid="viewer-note-delete-btn"
                  >
                    <i className="fas fa-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {resource.searchTermLists?.length > 0 && (
        <>
          <hr />
          <h4>Search Terms</h4>
          <div data-testid="viewer-search-term-list">
            {resource.searchTermLists.map((list) => {
              const expanded = expandedLists.has(list.id);
              return (
                <React.Fragment key={list.id}>
                  <div
                    style={{ padding: 7, height: 35, boxSizing: 'border-box', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                    data-testid="viewer-search-term-group-item"
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#d9edf7')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '')}
                    onClick={() => setExpandedLists((prev) => {
                      const next = new Set(prev);
                      expanded ? next.delete(list.id) : next.add(list.id);
                      return next;
                    })}
                  >
                    <i className={`fas fa-caret-${expanded ? 'down' : 'right'}`} style={{ marginRight: 6, fontSize: 16 }} />
                    {list.name}
                  </div>
                  {expanded && list.searchTerms.map((st) => (
                    <div
                      key={st.id}
                      data-testid="viewer-search-term-item"
                      style={{
                        padding: 7,
                        height: 35,
                        boxSizing: 'border-box',
                        paddingLeft: 28,
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        backgroundColor: selectedTermId === st.id ? '#d9edf7' : '',
                      }}
                      onMouseEnter={(e) => { if (selectedTermId !== st.id) e.currentTarget.style.backgroundColor = '#eef6fb'; }}
                      onMouseLeave={(e) => { if (selectedTermId !== st.id) e.currentTarget.style.backgroundColor = ''; }}
                      onClick={() => {
                        setSelectedTermId(st.id);
                        setSelectedSnippetIdx(null);
                        onSearchTermSelect?.(st.term);
                      }}
                    >
                      {!st.isValid && <i className="fas fa-exclamation-triangle text-danger" style={{ marginRight: 4 }} title={st.errorMessage} />}
                      {st.term}
                      {st.isValid && <i className="fas fa-search" style={{ marginLeft: 'auto', opacity: 0.4, fontSize: 11 }} />}
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </div>

          {selectedTermId && (
            <div className="iio-highlights">
              <br />
              <h4>
                {resource.searchTermLists.flatMap(l => l.searchTerms).find(st => st.id === selectedTermId)?.term}
              </h4>
              {highlightsFetching ? (
                <p className="text-muted">
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 4 }} />
                  Loading…
                </p>
              ) : (
                <div className="list-group" data-testid="viewer-term-highlight-list">
                  {highlights.map((snippet, idx) => (
                    <a
                      key={idx}
                      data-testid="viewer-term-highlight-item"
                      className={`list-group-item${selectedSnippetIdx === idx ? ' active' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        setSelectedSnippetIdx(idx);
                        onSearchTermSelect?.(snippet);
                      }}
                    >
                      {renderSnippet(snippet)}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Hits for the search that surfaced this resource. Independent of the search-term
          lists above — a resource with no term lists still gets these. */}
      {searchHits.length > 0 && (
        <div className="iio-highlights">
          <hr />
          <br />
          <h4>Search Hits</h4>
          <div className="list-group" data-testid="viewer-search-hit-list">
            {searchHits.map((snippet, idx) => (
              <a
                key={idx}
                data-testid="viewer-search-hit-item"
                className={`list-group-item${selectedHitIdx === idx ? ' active' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  setSelectedHitIdx(idx);
                  onSearchTermSelect?.(snippet);
                }}
              >
                {renderSnippet(snippet)}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
